import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Search, Sparkles, Kanban, FileSearch, Building2, LogOut, Bell,
  Map as MapIcon, CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/opportunites", label: "Opportunités", icon: Search, testid: "nav-opportunities" },
  { to: "/carte", label: "Carte", icon: MapIcon, testid: "nav-map" },
  { to: "/recherche-ia", label: "Recherche IA", icon: Sparkles, testid: "nav-ai-search" },
  { to: "/crm", label: "CRM", icon: Kanban, testid: "nav-crm" },
  { to: "/appels-offres", label: "Appels d'offres", icon: FileSearch, testid: "nav-tenders" },
  { to: "/entreprise", label: "Mon entreprise", icon: Building2, testid: "nav-company" },
  { to: "/tarifs", label: "Abonnement", icon: CreditCard, testid: "nav-pricing" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const tier = user?.subscription_tier || "FREE";

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="dashboard-layout">
      <aside className="w-64 border-r border-border bg-card/40 fixed inset-y-0 left-0 flex flex-col z-40">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-slate-950" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-sm leading-tight">CLEANING</div>
              <div className="font-display font-bold tracking-tight text-sm leading-tight text-cyan-400">CONTRACT AI</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">
              {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-mono mb-3">Plan · {tier}</div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-foreground" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      <div className="flex-1 ml-64">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between px-8">
          <div className="text-sm text-muted-foreground">
            <span className="text-emerald-400 font-medium">●</span> Système actif — données de démonstration
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" data-testid="notifications-button"><Bell className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="p-8">{children}</main>
      </div>
    </div>
  );
}
